'use client';

import * as ENGINE from '@gnsx/genesys.js';
import { type Instance, t } from 'mobx-state-tree';

import { getClassDefaultOptions } from '@/editor/core/classHelpers';

const DEBUG_LOG = {
  logClassInfos: false,
  logClassDefaultOptions: false,
  logCreateInstanceError: false,
  logCreateDefaultInstance: false,
} as const;


export const ClassInfo = t
  .model('ClassInfo', {
    className: t.identifier,
    displayName: t.string,
    category: t.string,
    type: t.string,
    description: t.maybe(t.string),
  })
  .volatile(self => ({
    classDefaultObject: undefined as ENGINE.Actor | ENGINE.SceneComponent | ENGINE.Resource | null | undefined
  }))
  .views(self => ({
    getConstructor(): any {
      return (ENGINE.ClassRegistry as any).registry.get(self.className);
    },
    get shortClassName(): string {
      return self.className.split('.').pop() ?? self.className;
    },
    get classIcon(): string | null | undefined {
      return self.classDefaultObject?.getEditorClassIcon();
    }
  }))
  .actions(self => ({
    afterCreate() {
      self.classDefaultObject = this.createInstance();
    },
    /** Drops the cached default instance so it becomes GC-eligible. Call
     *  before removing this `ClassInfo` from an MST map (e.g. during
     *  `refreshClassInfos`) — MST otherwise retains the old object across
     *  refreshes, leaking one default per engine class per build. */
    releaseDefaultObject() {
      self.classDefaultObject = undefined;
    },
    createInstance(...args: any[]) {
      if (DEBUG_LOG.logClassInfos) console.trace('[ClassInfo] createInstance', self.className, ...args);
      if (DEBUG_LOG.logClassDefaultOptions) console.log('[ClassInfo] defaultOptions', self.className, getClassDefaultOptions(self.className));
      try {
        if (args.length === 0) {
          const rawDefaultOptions = getClassDefaultOptions(self.className);
          const defaultOptions =
            rawDefaultOptions && typeof rawDefaultOptions === 'object' && !Array.isArray(rawDefaultOptions)
              ? rawDefaultOptions
              : {};
          return ENGINE.ClassRegistry.constructObject(self.className, defaultOptions);
        }
        return ENGINE.ClassRegistry.constructObject(self.className, ...args);
        // return ENGINE.ClassRegistry.constructObject(self.className, ...args);
      } catch (error) {
        if (DEBUG_LOG.logCreateInstanceError) console.error('[ClassInfo] Failed to create instance', self.className, error);
        return undefined;
      }
    },
  }));

export interface IClassInfo extends Instance<typeof ClassInfo> { }